import type { Controls, TeachStep } from "@obean/game-contract";
import { useSignal } from "@preact/signals";
import { useMemo } from "preact/hooks";
import { InputWatch, Teacher, lineOf, markTaught, taught } from "./teach.js";

/**
 * A game's first play on this page (P5-UX-05): the teacher if this browser hasn't had it yet, a
 * `see` for each sample of what the player did, and when the game first took their input (for
 * `first_move`). A new match calls `restart` so its first move is timed afresh. `see` runs inside
 * the game loop, so it only bumps a signal when a step changes.
 */
export function useTeach(gameId: string, steps: readonly TeachStep[]) {
  const version = useSignal(0);
  const teacher = useMemo(
    () => (steps.length > 0 && !taught(gameId) ? new Teacher(steps, performance.now()) : null),
    [gameId],
  );
  const first = useMemo(() => ({ at: null as number | null, watch: new InputWatch() }), [gameId]);
  const finish = () => {
    markTaught(gameId);
    version.value++;
  };
  const see = (controls: Controls, input: unknown, now: number): number | null => {
    const gave = first.watch.changed(input);
    const firstMove = gave && first.at === null ? (first.at = now) : null;
    if (teacher?.see(controls, gave, now)) {
      if (!teacher.current()) markTaught(gameId);
      version.value++;
    }
    return firstMove;
  };
  const skip = () => {
    teacher?.skip();
    finish();
  };
  const restart = () => {
    first.at = null;
    first.watch = new InputWatch();
  };
  return { teacher, version, see, skip, restart, teaching: teacher !== null };
}

/** Whether the card shows now (the page moves the game down for it). Reading it redraws on a step. */
export const teachingNow = (teach: ReturnType<typeof useTeach>) =>
  teach.version.value >= 0 && (teach.teacher?.current() ?? null) !== null;

/** The step showing, over the top of the game (on `--over-game`), with a way to put it away. */
export function TeachCard({
  teach,
  touch,
}: {
  teach: ReturnType<typeof useTeach>;
  touch: boolean;
}) {
  // Reading the version redraws this when a step changes.
  const now = teach.version.value >= 0 ? teach.teacher?.current() : null;
  if (!now) return null;
  return (
    <div class="teach" role="status" aria-live="polite">
      <span class="teach-count">
        {now.n} of {now.of}
      </span>
      <p>
        {now.done && <span aria-hidden="true">✓ </span>}
        {lineOf(now.step, touch)}
      </p>
      <button type="button" onClick={teach.skip}>
        Got it
      </button>
    </div>
  );
}
